/**
 * Saved Searches drawer (STN client round 2026-08, replaces the mockup's
 * "Projects"): every search is auto-saved and listed here newest-first with
 * its LLM-generated, user-editable title. No create button and no membership
 * editing — a saved search is an immutable result set; a row click restores
 * it wholesale (SavedSearchesService.RequestRestore → search page).
 *
 * Hosted in the app shell (works from any view); open/close state lives in
 * SavedSearchesService so the header pill can toggle it. Navigation back to
 * the search view goes through the host adapter (route or surface view state).
 */
import { ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAPS_HOST_ADAPTER, MapsHostAdapter } from '../../host-adapter';
import { SavedSearchesService } from '../../services/saved-searches.service';
import { NotifyError, NotifySuccess } from '../../services/notify';
import { FormatDisplayDate } from '../../utils/dates';
import type { MapsSavedSearch } from '../../models/maps.types';

const PAGE = 50;

interface DrawerRow {
    searchId: string;
    title: string;
    /** Pre-formatted (never a method call in the template — NG0100). */
    dateText: string | null;
    resultCount: number;
}

@Component({
    selector: 'maps-saved-searches-drawer',
    standalone: false,
    templateUrl: './saved-searches-drawer.component.html',
    styleUrl: './saved-searches-drawer.component.scss',
})
export class SavedSearchesDrawerComponent implements OnInit, OnDestroy {
    open = false;
    rows: DrawerRow[] = [];
    totalCount = 0;
    isLoadingMore = false;
    /** Row currently in inline title edit, with its draft text. */
    editingId: string | null = null;
    editDraft = '';

    private subscriptions: Subscription[] = [];
    /** Rows loaded past the shared first page (Load more). */
    private extraRows: DrawerRow[] = [];

    constructor(
        private readonly savedSearches: SavedSearchesService,
        @Inject(MAPS_HOST_ADAPTER) private readonly hostAdapter: MapsHostAdapter,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.savedSearches.DrawerOpen$.subscribe((open) => {
                this.zone.run(() => {
                    this.open = open;
                    if (!open) this.CancelEdit();
                    this.cdr.detectChanges();
                });
            }),
            this.savedSearches.Page$.subscribe((page) => {
                this.zone.run(() => {
                    if (page) {
                        this.rows = [...page.items.map(toRow), ...this.extraRows];
                        this.totalCount = page.totalCount;
                    }
                    this.cdr.detectChanges();
                });
            })
        );
    }

    ngOnDestroy(): void {
        for (const s of this.subscriptions) s.unsubscribe();
    }

    get hasMore(): boolean {
        return this.rows.length < this.totalCount;
    }

    OnClosed(): void {
        this.savedSearches.CloseDrawer();
    }

    RestoreRow(row: DrawerRow): void {
        if (this.editingId === row.searchId) return; // row is in edit mode
        this.savedSearches.RequestRestore(row.searchId);
        if (this.hostAdapter.CurrentLocation !== 'search') {
            this.hostAdapter.NavigateTo('search');
        }
    }

    async LoadMore(): Promise<void> {
        if (this.isLoadingMore || !this.hasMore) return;
        this.isLoadingMore = true;
        try {
            const page = await this.savedSearches.List(PAGE, this.rows.length);
            this.zone.run(() => {
                this.extraRows = [...this.extraRows, ...page.items.map(toRow)];
                this.rows = [...this.rows, ...page.items.map(toRow)];
                this.totalCount = page.totalCount;
                this.cdr.detectChanges();
            });
        } catch {
            NotifyError('Could not load more searches');
        } finally {
            this.zone.run(() => {
                this.isLoadingMore = false;
                this.cdr.detectChanges();
            });
        }
    }

    // ------------------------------------------------------ inline title edit

    StartEdit(row: DrawerRow, event: Event): void {
        event.stopPropagation(); // don't trigger the row's restore
        this.editingId = row.searchId;
        this.editDraft = row.title;
    }

    CancelEdit(): void {
        this.editingId = null;
        this.editDraft = '';
    }

    /** Enter/blur commits; Escape cancels (wired in the template). A draft
     *  equal to the current title is a silent cancel, not a mutation. */
    async CommitEdit(row: DrawerRow): Promise<void> {
        if (this.editingId !== row.searchId) return;
        const draft = this.editDraft.trim();
        this.CancelEdit();
        if (draft.length === 0 || draft === row.title) return;
        try {
            const updated = await this.savedSearches.UpdateTitle(row.searchId, draft);
            this.zone.run(() => {
                this.patchRowTitle(row.searchId, updated.title);
                NotifySuccess('Title updated');
                this.cdr.detectChanges();
            });
            // Keep chips/badge consumers in sync (silent refresh).
            void this.savedSearches.Refresh();
        } catch {
            NotifyError('Could not update the title');
        }
    }

    private patchRowTitle(searchId: string, title: string): void {
        this.rows = this.rows.map((r) => (r.searchId === searchId ? { ...r, title } : r));
        this.extraRows = this.extraRows.map((r) => (r.searchId === searchId ? { ...r, title } : r));
    }

    TrackByRow(_ix: number, row: DrawerRow): string {
        return row.searchId;
    }
}

function toRow(s: MapsSavedSearch): DrawerRow {
    return {
        searchId: s.searchId,
        title: s.title,
        dateText: FormatDisplayDate(s.timestamp),
        resultCount: s.resultCount,
    };
}
